/**
 * SystemBackupManager Component
 *
 * Admin component for triggering and downloading all-in-one system backups.
 *
 * @module SystemBackupManager
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Archive,
  CheckCircle2,
  Clock,
  Database,
  Download,
  HardDrive,
  Loader2,
  Play,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  downloadSystemBackupFile,
  formatBytes,
  getSystemBackupStatus,
  listSystemBackupFiles,
  runSystemBackup,
  type SystemBackupFileInfo,
  type SystemBackupRunInfo,
  type SystemBackupStatusResponse,
} from '@/lib/backupApi';
import { logger } from '@/lib/logger';

const POLL_INTERVAL_MS = 3_000;

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error) {
    const message = error.message;
    if (typeof message === 'string') return message;
  }
  return 'An unknown error occurred';
};

const formatDateTime = (value?: string | null): string => {
  if (!value) return 'Not available';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not available';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const formatTrigger = (trigger?: string | null): string => {
  if (!trigger) return 'Unknown';
  return trigger.charAt(0).toUpperCase() + trigger.slice(1);
};

const getRunSummary = (run?: SystemBackupRunInfo | null): string => {
  if (!run) return 'No completed run yet';
  if (run.state === 'failed') return run.error || 'Failed';
  if (run.filename) return run.filename;
  return formatDateTime(run.finished_at || run.started_at);
};

const getStateBadgeVariant = (
  state?: SystemBackupRunInfo['state'] | SystemBackupStatusResponse['state'],
): 'default' | 'secondary' | 'destructive' | 'outline' => {
  if (state === 'failed') return 'destructive';
  if (state === 'succeeded') return 'default';
  if (state === 'running') return 'secondary';
  return 'outline';
};

const createDownload = (blob: Blob, filename: string) => {
  const url = globalThis.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  globalThis.URL.revokeObjectURL(url);
};

export default function SystemBackupManager() {
  const [status, setStatus] = useState<SystemBackupStatusResponse | null>(null);
  const [files, setFiles] = useState<SystemBackupFileInfo[]>([]);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [runLoading, setRunLoading] = useState(false);
  const [downloadingFilename, setDownloadingFilename] = useState<string | null>(null);

  const isRunning = status?.state === 'running';

  const refreshFiles = useCallback(async () => {
    const fileResponse = await listSystemBackupFiles();
    setFiles(fileResponse.items);
  }, []);

  const refreshAll = useCallback(async (showRefresh = false) => {
    try {
      if (showRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const [statusResponse, fileResponse] = await Promise.all([
        getSystemBackupStatus(),
        listSystemBackupFiles(),
      ]);
      setStatus(statusResponse);
      setFiles(fileResponse.items);
      setStatusMessage(null);
    } catch (error) {
      const message = getErrorMessage(error);
      logger.error('Failed to load system backups:', message);
      setStatusMessage(`Failed to load system backups: ${message}`);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    if (!isRunning) return;

    const interval = globalThis.setInterval(() => {
      void (async () => {
        try {
          const nextStatus = await getSystemBackupStatus();
          setStatus(nextStatus);
          if (nextStatus.state !== 'running') {
            await refreshFiles();
          }
        } catch (error) {
          const message = getErrorMessage(error);
          logger.error('Failed to poll system backup status:', message);
        }
      })();
    }, POLL_INTERVAL_MS);

    return () => globalThis.clearInterval(interval);
  }, [isRunning, refreshFiles]);

  const handleRunBackup = async () => {
    try {
      setRunLoading(true);
      setStatusMessage(null);
      const response = await runSystemBackup();
      setStatus((currentStatus) =>
        currentStatus
          ? {
              ...currentStatus,
              state: response.state,
              current_run: response.current_run,
            }
          : null,
      );
      toast.success(response.message);
      await refreshAll(true);
    } catch (error) {
      const message = getErrorMessage(error);
      logger.error('Failed to start system backup:', message);
      setStatusMessage(message);
      toast.error(message);
    } finally {
      setRunLoading(false);
    }
  };

  const handleDownload = async (file: SystemBackupFileInfo) => {
    try {
      setDownloadingFilename(file.filename);
      const blob = await downloadSystemBackupFile(file.filename);
      createDownload(blob, file.filename);
      toast.success('Backup download started');
    } catch (error) {
      const message = getErrorMessage(error);
      logger.error('Failed to download system backup:', message);
      setStatusMessage(`Download failed: ${message}`);
      toast.error(message);
    } finally {
      setDownloadingFilename(null);
    }
  };

  const summaryCards = useMemo(
    () => [
      {
        label: 'Current state',
        value: status?.state === 'running' ? 'Running' : 'Idle',
        icon: status?.state === 'running' ? Loader2 : CheckCircle2,
      },
      {
        label: 'Next scheduled run',
        value: formatDateTime(status?.next_scheduled_run_at),
        icon: Clock,
      },
      {
        label: 'Last run',
        value: getRunSummary(status?.last_run),
        icon: status?.last_run?.state === 'failed' ? AlertTriangle : Archive,
      },
      {
        label: 'Retention',
        value: `${status?.retention_days ?? 4} days`,
        icon: HardDrive,
      },
    ],
    [status],
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="font-ui text-2xl font-bold text-foreground">
            System Backups
          </h2>
          <p className="font-ui text-sm text-muted-foreground">
            All-in-one archives with media files, database dump, and manifest metadata.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void refreshAll(true)}
          disabled={refreshing || loading}
        >
          {refreshing ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          <span className="font-ui">Refresh</span>
        </Button>
      </div>

      {statusMessage && (
        <Alert variant={statusMessage.includes('failed') ? 'destructive' : 'default'}>
          <AlertDescription className="font-ui">{statusMessage}</AlertDescription>
        </Alert>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {summaryCards.map(({ label, value, icon: Icon }) => (
          <Card key={label}>
            <CardContent className="flex items-start gap-3 p-4">
              <div className="mt-0.5 rounded-md bg-muted p-2 text-muted-foreground">
                <Icon
                  className={`h-4 w-4 ${label === 'Current state' && isRunning ? 'animate-spin' : ''}`}
                />
              </div>
              <div className="min-w-0 space-y-1">
                <p className="font-ui text-xs font-medium uppercase text-muted-foreground">
                  {label}
                </p>
                <p className="font-ui truncate text-sm font-semibold text-foreground">
                  {loading ? 'Loading...' : value}
                </p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader className="space-y-1">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <CardTitle className="font-ui flex items-center gap-2 text-lg">
                <Database className="h-5 w-5 text-primary" />
                Manual Backup
              </CardTitle>
              <CardDescription className="font-ui">
                Uses the same media and PostgreSQL backup path as the midnight job.
              </CardDescription>
            </div>
            <Badge variant={getStateBadgeVariant(status?.state)} className="font-ui w-fit">
              {status?.state === 'running' ? 'Running' : 'Idle'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="font-ui text-sm text-muted-foreground">
            {status?.current_run
              ? `Started ${formatDateTime(status.current_run.started_at)}`
              : `Backup root: ${status?.backup_root || 'Not available'}`}
          </div>
          <Button onClick={handleRunBackup} disabled={runLoading || isRunning}>
            {runLoading || isRunning ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Play className="mr-2 h-4 w-4" />
            )}
            <span className="font-ui">
              {isRunning ? 'Backup running' : 'Run backup now'}
            </span>
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="space-y-1">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <CardTitle className="font-ui flex items-center gap-2 text-lg">
                <Archive className="h-5 w-5 text-primary" />
                Available Backups
              </CardTitle>
              <CardDescription className="font-ui">
                {files.length} generated archive{files.length === 1 ? '' : 's'} available
              </CardDescription>
            </div>
            {status?.last_run?.state && (
              <Badge
                variant={getStateBadgeVariant(status.last_run.state)}
                className="font-ui w-fit"
              >
                Last run {status.last_run.state}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              <span className="font-ui text-sm">Loading backups...</span>
            </div>
          ) : files.length === 0 ? (
            <div className="rounded-md border border-dashed p-8 text-center">
              <Archive className="mx-auto h-8 w-8 text-muted-foreground" />
              <p className="font-ui mt-3 text-sm font-medium text-foreground">
                No system backups yet
              </p>
              <p className="font-ui mt-1 text-sm text-muted-foreground">
                Generated backup archives will appear here.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-ui">File</TableHead>
                  <TableHead className="font-ui">Created</TableHead>
                  <TableHead className="font-ui">Size</TableHead>
                  <TableHead className="font-ui">Trigger</TableHead>
                  <TableHead className="font-ui">Contents</TableHead>
                  <TableHead className="font-ui text-right">Download</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {files.map((file) => (
                  <TableRow key={file.filename}>
                    <TableCell className="font-ui max-w-[18rem] truncate font-medium">
                      {file.filename}
                    </TableCell>
                    <TableCell className="font-ui text-muted-foreground">
                      {formatDateTime(file.created_at)}
                    </TableCell>
                    <TableCell className="font-ui text-muted-foreground">
                      {formatBytes(file.size_bytes)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-ui">
                        {formatTrigger(file.trigger)}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-ui text-muted-foreground">
                      {file.manifest_available ? (
                        <span>
                          Media: {file.media_file_count ?? 0} files - DB:{' '}
                          {file.database_name || 'Unknown'}
                        </span>
                      ) : (
                        <span>Manifest unavailable</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => void handleDownload(file)}
                        disabled={downloadingFilename === file.filename}
                      >
                        {downloadingFilename === file.filename ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                        <span className="sr-only font-ui">Download {file.filename}</span>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
